import { Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import CLoading from "../../components/common/CLoading";
import RouterArr from "./router";
import { isUserAdmin } from "@/utils/jwtHelper";
import { isAuthenticated, tokenSubject, ACCESS_KEY } from "@/api/HttpClient";
import { useSelectorRoot } from "@/store/hooks";

const renderRoutes = (routes: any[], authed: boolean) =>
  routes.map(({ path, component: Component, auth, requireAdmin, children, ...rest }) => {
    // Check admin permission if required
    if (requireAdmin && !isUserAdmin()) {
      return (
        <Route 
          path={path} 
          element={<Navigate to="/page/drive" replace />} 
          key={path || "root"} 
          {...rest}
        />
      );
    }

    const element = auth ? (
      authed ? <Component /> : <Navigate to="/auth/login" replace />
    ) : (
      <Component />
    );

    return (
      <Route path={path} element={element} key={path || "root"} {...rest}>
        {children && renderRoutes(children, authed)}
      </Route>
    );
  });

const MainRouter = (): React.ReactElement => {
  const [authed, setAuthed] = useState(isAuthenticated());
  const flowStatus = useSelectorRoot((state) => state.drive.flowStatus);

  useEffect(() => {
    const sub = tokenSubject.subscribe((token) => {
      setAuthed(!!token);
    });
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_KEY && e.newValue === null) {
        console.log("Token removed in another tab, logging out...");
        tokenSubject.next(null);
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <Router>
      <Suspense fallback={<CLoading visible={true} fullScreen={true} />}>
        <Routes>
          {authed ? (
            <>
              <Route path="/" element={<Navigate to="/page/drive" replace />} />
              {/* Don't auto-redirect from login until drive flow is ready */}
              <Route path="/auth/login" element={
                flowStatus === "ready" ? <Navigate to="/page/drive" replace /> : <CLoading visible={true} fullScreen={true} />
              } />
              {renderRoutes(RouterArr, authed)}
            </>
          ) : (
            <>
            <Route path="*" element={<Navigate to="/auth/login" replace />} />
            {renderRoutes(
                RouterArr.filter((r) => r.path.startsWith("/auth")),
                authed
              )}
            </>
          )}
        </Routes>
      </Suspense>
    </Router>
  );
};

export default MainRouter;
